import { ChevronDown } from 'lucide-react';

import { Skeleton } from '~/components/ui/skeleton';
import { cn } from '~/utils/cn';
import { formatDateTime } from '~/utils/date';

import { useProductAttempts } from '../../hooks/use-batch-retry';
import type { BatchRun, RetryItemOutcome } from '../../types/batch-retry.api.types';

import { formatCount, humanize } from './batch-retry-utils';

const OUTCOME_LABEL: Record<RetryItemOutcome, string> = {
  PENDING: 'Pending',
  SUCCEEDED: 'Enriched',
  FAILED: 'Failed',
  NOT_ATTEMPTED: 'Not reached',
  SKIPPED: 'Skipped · no change',
};

const OUTCOME_TONE: Record<RetryItemOutcome, string> = {
  PENDING: 'bg-info-50 text-info',
  SUCCEEDED: 'bg-emerald-50 text-emerald-700',
  FAILED: 'bg-error-50 text-error',
  NOT_ATTEMPTED: 'bg-warning-50 text-warning',
  SKIPPED: 'bg-muted text-muted-foreground',
};

const OUTCOME_DOT: Record<RetryItemOutcome, string> = {
  PENDING: 'bg-info',
  SUCCEEDED: 'bg-emerald-500',
  FAILED: 'bg-error',
  NOT_ATTEMPTED: 'bg-warning',
  SKIPPED: 'bg-neutral-400',
};

export function OutcomePill({ outcome, testId }: { outcome: RetryItemOutcome; testId?: string }) {
  return (
    <span
      data-testid={testId}
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        OUTCOME_TONE[outcome],
      )}
    >
      {OUTCOME_LABEL[outcome]}
    </span>
  );
}

type OutcomeCounts = Pick<BatchRun, 'succeeded' | 'failed' | 'notAttempted' | 'skipped'>;

const SEGMENTS = [
  { key: 'succeeded', label: 'Enriched', dot: 'bg-emerald-500' },
  { key: 'failed', label: 'Failed', dot: 'bg-error' },
  { key: 'notAttempted', label: 'Not reached', dot: 'bg-warning' },
  { key: 'skipped', label: 'Skipped', dot: 'bg-neutral-400' },
] as const;

/** Segment widths (%) against `total`; the shared denominator is never below their sum. */
function outcomeSegments(run: OutcomeCounts, total: number) {
  const sum = SEGMENTS.reduce((acc, s) => acc + (run[s.key] ?? 0), 0);
  const denom = Math.max(total, sum);
  return SEGMENTS.map((s) => ({
    ...s,
    value: run[s.key] ?? 0,
    pct: denom > 0 ? ((run[s.key] ?? 0) / denom) * 100 : 0,
  }));
}

/** Stacked outcome bar: one coloured segment per non-zero count. */
export function RunOutcomeBar({
  run,
  total,
  testId,
  label,
  className,
}: {
  run: OutcomeCounts;
  total: number;
  testId?: string;
  label: string;
  className?: string;
}) {
  return (
    <div
      role="img"
      aria-label={label}
      data-testid={testId}
      className={cn('bg-muted flex gap-0.5 overflow-hidden rounded-full', className ?? 'h-2')}
    >
      {outcomeSegments(run, total)
        .filter((s) => s.value > 0)
        .map((s) => (
          <span
            key={s.key}
            data-testid={testId ? `${testId}-${s.key}` : undefined}
            className={cn('h-full', s.dot)}
            style={{ width: `${String(s.pct)}%` }}
          />
        ))}
    </div>
  );
}

/** Numbers row under the bar: a dot matching each segment colour before its count. */
export function RunOutcomeLegend({
  run,
  className,
  compact = false,
  dotClassName,
}: {
  run: OutcomeCounts;
  className?: string;
  /** Dots and counts only (labels move to the tooltip and screen readers) — for tight rows. */
  compact?: boolean;
  /** Overrides the dot size, for a legend set in larger text. */
  dotClassName?: string;
}) {
  return (
    <>
      {SEGMENTS.filter((s) => s.key !== 'skipped' || (run.skipped ?? 0) > 0).map((s) => {
        const value = run[s.key] ?? null;
        return (
          <span
            key={s.key}
            className={cn('inline-flex items-center gap-1', className)}
            title={`${s.label}: ${formatCount(value)}`}
          >
            {value !== null && (
              <span className={cn('size-2 rounded-full', s.dot, dotClassName)} aria-hidden="true" />
            )}
            <span className={compact ? 'sr-only' : undefined}>{s.label}</span>{' '}
            <b className="text-foreground">{formatCount(value)}</b>
          </span>
        );
      })}
    </>
  );
}

/** Horizontal bars, one per reason, scaled to the biggest. */
export function ReasonBars({
  reasons,
  testId,
}: {
  reasons: { key: string; label: string; count: number }[];
  testId?: string;
}) {
  const visible = reasons.filter((r) => r.count > 0);
  if (visible.length === 0) return null;
  const max = Math.max(...visible.map((r) => r.count));
  return (
    <ul data-testid={testId} className="space-y-1.5">
      {visible.map((r) => (
        <li key={r.key} className="grid grid-cols-[minmax(0,10rem)_1fr_auto] items-center gap-3">
          <span className="text-muted-foreground truncate text-xs" title={r.label}>
            {r.label}
          </span>
          <span className="bg-muted h-2 overflow-hidden rounded-full">
            <span
              className="bg-warning block h-2 rounded-full"
              style={{ width: `${String(Math.max(2, Math.round((r.count / max) * 100)))}%` }}
            />
          </span>
          <span className="text-xs font-medium tabular-nums">{r.count.toLocaleString()}</span>
        </li>
      ))}
    </ul>
  );
}

/** "Attempts" toggle; the parent renders {@link ProductAttemptsPanel} under the row. */
export function AttemptsToggle({
  productId,
  expanded,
  onToggle,
}: {
  productId: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={`attempts-toggle-${productId}`}
      aria-expanded={expanded}
      aria-controls={`attempts-${productId}`}
      onClick={onToggle}
      className="text-primary inline-flex cursor-pointer items-center gap-1 text-xs font-medium hover:underline"
    >
      Attempts
      <ChevronDown className={cn('size-3.5 transition-transform', expanded && 'rotate-180')} />
    </button>
  );
}

export function ProductAttemptsPanel({ jobId, productId }: { jobId: string; productId: string }) {
  const { data, isLoading, isError } = useProductAttempts(jobId, productId);
  return (
    <div
      id={`attempts-${productId}`}
      data-testid={`attempts-panel-${productId}`}
      className="bg-muted/40 space-y-2 rounded-md border p-3"
    >
      <p className="text-xs font-semibold">
        Every attempt · <span className="font-mono font-normal">{productId}</span>
      </p>
      {isLoading ? (
        <Skeleton className="h-10 w-full" />
      ) : isError || !data ? (
        <p className="text-error text-xs">Could not load the attempt history.</p>
      ) : (
        <ol className="space-y-1.5">
          {data.attempts.map((a) => (
            <li
              key={`${String(a.attempt)}-${a.runId ?? ''}`}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
            >
              <span className={cn('size-2 rounded-full', OUTCOME_DOT[a.outcome])} />
              <span className="font-medium">
                {a.attempt === 0 ? 'Original run' : `Retry #${String(a.attempt)}`}
              </span>
              <OutcomePill outcome={a.outcome} />
              {a.failureCategory && (
                <span className="text-muted-foreground">{humanize(a.failureCategory)}</span>
              )}
              {a.error && (
                <span className="text-muted-foreground max-w-full truncate" title={a.error}>
                  {a.error}
                </span>
              )}
              {a.at && (
                <span className="text-muted-foreground ml-auto">{formatDateTime(a.at)}</span>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
